import { ParsedVendor, PriceRange, ROLE_TO_SERVICES } from './vendorData';
import { formatDateTime } from '../utils/formatDate';
import { getRatings } from '../utils/ratingStorage';

export interface ScrapedVendorData {
  name: string;
  serviceType: string;
  articleIds: number[];
  firstSeen: string;
  lastSeen: string;
  articleCount: number;
}

export interface ScrapedDataFile {
  totalVendors: number;
  vendors: ScrapedVendorData[];
  scrapedAt: string;
  source: string;
  metadata: {
    totalPages: number;
    delayBetweenRequests: number;
    userAgent: string;
    focus: string;
  };
}

// Get real user ratings for a vendor
async function getRealVendorRating(vendorId: string, vendorName: string): Promise<{ rating: number; reviewCount: number }> {
  try {
    const userRatings = await getRatings();
    const vendorRatings = userRatings.filter(r =>
      r.vendorId === vendorId ||
      r.vendorName.toLowerCase().includes(vendorName.toLowerCase()) ||
      vendorName.toLowerCase().includes(r.vendorName.toLowerCase())
    );

    if (vendorRatings.length === 0) {
      return { rating: 0, reviewCount: 0 };
    }

    const totalRating = vendorRatings.reduce((sum, r) => sum + r.rating, 0);
    const averageRating = totalRating / vendorRatings.length;

    return {
      rating: Math.round(averageRating * 10) / 10,
      reviewCount: vendorRatings.length
    };
  } catch (error) {
    console.error('Error getting real vendor ratings:', error);
    return { rating: 0, reviewCount: 0 };
  }
}

// Convert scraped BellaNaija data to ParsedVendor format
export async function convertScrapedDataToVendors(scrapedData: ScrapedDataFile, articlesData?: any[]): Promise<ParsedVendor[]> {
  const vendors: ParsedVendor[] = [];

  for (let vendorIndex = 0; vendorIndex < scrapedData.vendors.length; vendorIndex++) {
    const vendorData = scrapedData.vendors[vendorIndex];
    const cleanHandle = vendorData.name.replace(/^[@#]/, '').trim();
    const role = vendorData.serviceType;

    // Map role to services
    const services = ROLE_TO_SERVICES[role] || ['General Services'];

    // Get article details for this vendor
    const vendorArticles = articlesData ?
      vendorData.articleIds.map(id => articlesData.find(article => article.id === id)).filter(Boolean) :
      [];

    // Get real user ratings for this vendor
    const realRatings = await getRealVendorRating(`scraped-${vendorIndex + 1}-${role.toLowerCase()}`, formatVendorName(cleanHandle));
    
    // Generate vendor data
    const vendor: ParsedVendor = {
      id: `scraped-${vendorIndex + 1}-${role.toLowerCase()}`,
      name: formatVendorName(cleanHandle),
      services: services.slice(0, 3), // Take first 3 services
      description: `Professional ${formatServiceType(role)} services featured in BellaNaija articles. Specializing in ${formatServiceType(role)} for weddings and special events. Featured in ${vendorData.articleCount} article${vendorData.articleCount > 1 ? 's' : ''}.`,
      contact: {
        instagram: `https://www.instagram.com/${cleanHandle}/`,
        website: undefined // Don't set website if we only have Instagram
      },
      rating: realRatings.rating,
      reviewCount: realRatings.reviewCount,
      priceRange: getStablePriceRange(cleanHandle),
      availability: getStableAvailability(cleanHandle),
      specializations: getRandomSpecializations(role),
      images: [generatePlaceholderImage(cleanHandle)],
      verified: true, // Scraped from BellaNaija, so considered verified
      featured: vendorData.articleCount > 3, // Featured if mentioned in multiple articles
      createdAt: new Date(vendorData.firstSeen),
      sourceEvents: vendorArticles,
      instagramHandle: cleanHandle
    };

    vendors.push(vendor);
  }

  return vendors;
}

// Load scraped data from JSON file
export async function loadScrapedVendorData(): Promise<ParsedVendor[]> {
  try {
    // Try to load the scraped data from the correct file
    const [vendorResponse, articlesResponse] = await Promise.all([
      fetch('/data/bellanaija_credits.json'),
      fetch('/data/scraped_articles_list.json')
    ]);

    if (!vendorResponse.ok) {
      console.warn('Scraped vendor data file not found, using empty array');
      return [];
    }

    const scrapedData: ScrapedDataFile = await vendorResponse.json();
    const articlesData = articlesResponse.ok ? await articlesResponse.json() : [];

    return await convertScrapedDataToVendors(scrapedData, articlesData);
  } catch (error) {
    console.error('Error loading scraped vendor data:', error);
    return [];
  }
}

// Helper functions
function formatVendorName(handle: string): string {
  // Convert Instagram handle to a readable name
  return handle
    .replace(/[._]/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase())
    .trim();
}

function formatServiceType(role: string): string {
  // Convert role to a more readable service type
  const serviceTypeMap: Record<string, string> = {
    'Photography': 'Photography',
    'Photographer': 'Photography',
    'Videography': 'Videography',
    'Videographer': 'Videography',
    'Makeup': 'Makeup',
    'Makeup Artist': 'Makeup',
    'Hairstylist': 'Hair Styling',
    'Hair Stylist': 'Hair Styling',
    'Hair': 'Hair Styling',
    'Location': 'Venue',
    'Venue': 'Venue',
    'Decor': 'Decor',
    'Florist': 'Floral Arrangements',
    'DJ': 'Entertainment',
    'Band': 'Entertainment',
    'conceptdesignplanningandstyling': 'Concept Design, Planning & Styling',
    'conceptdesignplanning': 'Concept Design & Planning',
    'conceptdesign': 'Concept Design',
    'planningandstyling': 'Planning & Styling',
    'planning': 'Event Planning',
    'styling': 'Styling'
  };
  
  // If exact match found, return it
  if (serviceTypeMap[role]) {
    return serviceTypeMap[role];
  }
  
  // Try to split concatenated words and format them
  const words = role.replace(/([a-z])([A-Z])/g, '$1 $2').split(' ');
  const formattedWords = words.map(word => 
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  );
  
  return formattedWords.join(' ');
}

/** Simple deterministic hash so values are stable across page loads. */
function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (Math.imul(31, h) + s.charCodeAt(i)) >>> 0;
  }
  return h;
}

function getStablePriceRange(seed: string): PriceRange {
  const ranges: PriceRange[] = ['₦', '₦₦', '₦₦₦', '₦₦₦₦'];
  return ranges[hashString(seed) % ranges.length];
}

function getStableAvailability(seed: string): string[] {
  const options = ['Weekdays', 'Weekends', 'Evenings', 'Holidays', 'Year-round'];
  const h = hashString(seed + '_avail');
  const count = (h % 3) + 2; // 2–4 items
  // Deterministic shuffle via Fisher-Yates with LCG
  const arr = [...options];
  let rng = h;
  for (let i = arr.length - 1; i > 0; i--) {
    rng = (rng * 1664525 + 1013904223) >>> 0;
    const j = rng % (i + 1);
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr.slice(0, count);
}

function getRandomSpecializations(role: string): string[] {
  const specializations: Record<string, string[]> = {
    'Photography': ['Wedding Photography', 'Portrait Photography', 'Event Photography', 'Engagement Shoots'],
    'Photographer': ['Wedding Photography', 'Portrait Photography', 'Event Photography', 'Engagement Shoots'],
    'Videography': ['Wedding Films', 'Event Videography', 'Cinematic Videos', 'Drone Footage'],
    'Videographer': ['Wedding Films', 'Event Videography', 'Cinematic Videos', 'Drone Footage'],
    'Makeup': ['Bridal Makeup', 'Editorial Makeup', 'Natural Looks', 'Glam Makeup'],
    'Makeup Artist': ['Bridal Makeup', 'Editorial Makeup', 'Natural Looks', 'Glam Makeup'],
    'Hairstylist': ['Bridal Hairstyling', 'Natural Hair', 'Hair Extensions', 'Updos'],
    'Hair Stylist': ['Bridal Hairstyling', 'Natural Hair', 'Hair Extensions', 'Updos'],
    'Hair': ['Bridal Hairstyling', 'Natural Hair', 'Hair Extensions', 'Updos'],
    'Location': ['Event Venues', 'Wedding Venues', 'Outdoor Spaces', 'Reception Halls'],
    'Venue': ['Event Venues', 'Wedding Venues', 'Outdoor Spaces', 'Reception Halls'],
    'Decor': ['Wedding Decor', 'Event Styling', 'Floral Arrangements', 'Table Settings'],
    'Florist': ['Wedding Flowers', 'Bridal Bouquets', 'Event Florals', 'Centerpieces'],
    'DJ': ['Wedding DJ', 'Event Music', 'Sound Systems', 'Entertainment'],
    'Band': ['Live Music', 'Wedding Entertainment', 'Event Performances', 'Musical Acts']
  };
  
  const options = specializations[role] || ['Event Services', 'Wedding Services', 'Professional Services'];
  const h = hashString(role + '_spec');
  const count = (h % 2) + 2;
  const arr = [...options];
  let rng = h;
  for (let i = arr.length - 1; i > 0; i--) {
    rng = (rng * 1664525 + 1013904223) >>> 0;
    const j = rng % (i + 1);
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr.slice(0, count);
}

function generatePlaceholderImage(handle: string): string {
  const colors = ['4F46E5', '7C3AED', '059669', 'DC2626', 'EA580C', 'BE185D', '0891B2', '059669'];
  const color = colors[hashString(handle) % colors.length];
  return `https://via.placeholder.com/300x200/${color}/FFFFFF?text=${encodeURIComponent(handle)}`;
}

// Get the last scrape timestamp
export function getLastScrapeTimestamp(): string | null {
  try {
    const stored = localStorage.getItem('lastScrapeTimestamp');
    return stored || null;
  } catch (error) {
    console.error('Error reading last scrape timestamp:', error);
    return null;
  }
}

// Set the last scrape timestamp
export function setLastScrapeTimestamp(timestamp: string): void {
  try {
    localStorage.setItem('lastScrapeTimestamp', timestamp);
  } catch (error) {
    console.error('Error setting last scrape timestamp:', error);
  }
}

// Format timestamp for display
export function formatScrapeTimestamp(timestamp: string): string {
  try {
    const date = new Date(timestamp);
    return formatDateTime(date);
  } catch (error) {
    console.error('Error formatting timestamp:', error);
    return 'Unknown';
  }
}
